import { useEffect, useState } from 'react';
import api from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { MapPin, Loader2, Search, Building2, KeyRound, Save, RefreshCw, Check, AlertTriangle, Lock, X, Zap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Settings > Integrations > Barbour — credentials (username / password / API
 * key, encrypted at rest via helpers/barbour_credentials.py, ALL masked in the
 * UI) plus a test search tool for finding Barbour ABI projects within a
 * radius of a postcode. Uses the same Barbour API access already connected
 * to the Zoho CRM "Barbour Project ID" automation.
 */
export const BarbourIntegrationSettings = () => {
  // --- Credentials state ---
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [editing, setEditing] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);

  // --- Search test tool state ---
  const [postcode, setPostcode] = useState('');
  const [radius, setRadius] = useState('5');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  // --- Direct-API vs Zoho-function toggle ---
  const [savingToggle, setSavingToggle] = useState(false);

  // --- Barbour Search V2 default radius ---
  const [v2Radius, setV2Radius] = useState('0.1');
  const [savingV2Radius, setSavingV2Radius] = useState(false);

  // --- Max radius allowed anywhere a Barbour search radius is used ---
  const [maxRadius, setMaxRadius] = useState('5');
  const [savingMaxRadius, setSavingMaxRadius] = useState(false);

  const fetchStatus = async () => {
    setLoadingStatus(true);
    try {
      const res = await api.get(`${API}/barbour/credentials-status`);
      setStatus(res.data);
      if (res.data?.v2_default_radius_miles != null) {
        setV2Radius(String(res.data.v2_default_radius_miles));
      }
      if (res.data?.max_radius_miles != null) {
        setMaxRadius(String(res.data.max_radius_miles));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load Barbour credential status');
    } finally {
      setLoadingStatus(false);
    }
  };
  useEffect(() => { fetchStatus(); }, []);

  const startEdit = () => {
    setUsername('');
    setPassword('');
    setApiKey('');
    setEditing(true);
  };
  const cancelEdit = () => {
    setEditing(false);
    setUsername(''); setPassword(''); setApiKey('');
  };

  const onSave = async () => {
    if (!username.trim() && !password.trim() && !apiKey.trim()) {
      toast.error('Enter at least one value to save');
      return;
    }
    setSaving(true);
    try {
      const payload = {};
      if (username.trim()) payload.username = username.trim();
      if (password.trim()) payload.password = password.trim();
      if (apiKey.trim()) payload.api_key = apiKey.trim();
      const res = await api.put(`${API}/barbour/credentials`, payload);
      setStatus(res.data);
      toast.success('Barbour ABI credentials saved');
      setEditing(false);
      setUsername(''); setPassword(''); setApiKey('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const search = async () => {
    if (!postcode.trim()) return;
    setSearching(true);
    setError('');
    setResults(null);
    try {
      const res = await api.post(`${API}/barbour/search`, {
        postcode: postcode.trim(),
        radius_miles: Math.min(parseFloat(radius) || 5, parseFloat(maxRadius) || 5),
      });
      setResults(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Search failed');
      toast.error(e?.response?.data?.detail || 'Barbour search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') search();
  };

  const onToggleDirectApi = async (checked) => {
    setSavingToggle(true);
    try {
      const res = await api.put(`${API}/barbour/use-direct-api`, { use_direct_api: checked });
      setStatus(res.data);
      toast.success(checked
        ? 'Now using the direct Barbour API everywhere — "Search Barbour" (V1) is visible again in the CRM dropdown.'
        : 'Now using the Zoho CRM function everywhere — "Search Barbour" (V1) is hidden from the CRM dropdown, Terra now uses the Zoho method too.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update the toggle');
    } finally {
      setSavingToggle(false);
    }
  };

  const onSaveV2Radius = async () => {
    const value = parseFloat(v2Radius);
    if (!value || value <= 0 || value > 100) {
      toast.error('Enter a radius between 0 and 100 miles');
      return;
    }
    setSavingV2Radius(true);
    try {
      const res = await api.put(`${API}/barbour/v2-default-radius`, { radius_miles: value });
      setStatus(res.data);
      toast.success(`Barbour Search V2 default radius saved — ${value} mile${value === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save the default radius');
    } finally {
      setSavingV2Radius(false);
    }
  };

  const onSaveMaxRadius = async () => {
    const value = parseFloat(maxRadius);
    if (!value || value <= 0 || value > 100) {
      toast.error('Enter a radius between 0 and 100 miles');
      return;
    }
    setSavingMaxRadius(true);
    try {
      const res = await api.put(`${API}/barbour/max-radius`, { radius_miles: value });
      setStatus(res.data);
      if (res.data?.v2_default_radius_miles != null) setV2Radius(String(res.data.v2_default_radius_miles));
      toast.success(`Barbour max radius saved — ${value} mile${value === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save the max radius');
    } finally {
      setSavingMaxRadius(false);
    }
  };

  const isConfigured = !!status?.configured;

  return (
    <div className="space-y-5" data-testid="barbour-integration-settings">
      <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
        <Building2 className="w-4 h-4" style={{ color: '#ee7203' }} />
        Barbour ABI Integration
      </h3>

      {/* Credentials card */}
      <div className="rounded-xl border bg-white p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              {loadingStatus ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />
              ) : isConfigured ? (
                <Check className="w-3.5 h-3.5 text-emerald-600" />
              ) : (
                <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
              )}
              API Credentials — {loadingStatus ? 'Checking…' : isConfigured ? 'Configured' : 'Not configured'}
            </h4>
            {status?.updated_at && (
              <p className="text-xs text-gray-500 mt-0.5">
                Last updated {new Date(status.updated_at).toLocaleString('en-GB')}{status.updated_by_email ? ` by ${status.updated_by_email}` : ''}
              </p>
            )}
            <p className="text-xs text-gray-500 mt-0.5">
              Same credentials already used by the CRM's Barbour Project ID automation.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchStatus} disabled={loadingStatus} data-testid="barbour-refresh-btn">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loadingStatus ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            {!editing && (
              <Button size="sm" onClick={startEdit} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="barbour-edit-btn">
                <KeyRound className="w-3.5 h-3.5 mr-1" /> {isConfigured ? 'Edit Credentials' : 'Add Credentials'}
              </Button>
            )}
            {editing && (
              <>
                <Button variant="outline" size="sm" onClick={cancelEdit}><X className="w-3.5 h-3.5 mr-1" /> Cancel</Button>
                <Button size="sm" onClick={onSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="barbour-save-btn">
                  {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save
                </Button>
              </>
            )}
          </div>
        </div>

        {!editing ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div className="flex items-center justify-between py-1.5 border-b border-gray-100 sm:border-b-0">
              <span className="text-gray-500">Username</span>
              <span className="font-mono text-gray-800">{status?.masked_username || '—'}</span>
            </div>
            <div className="flex items-center justify-between py-1.5 border-b border-gray-100 sm:border-b-0">
              <span className="text-gray-500">Password</span>
              <span className="font-mono text-gray-800">{status?.masked_password || '—'}</span>
            </div>
            <div className="flex items-center justify-between py-1.5">
              <span className="text-gray-500">API Key</span>
              <span className="font-mono text-gray-800">{status?.masked_api_key || '—'}</span>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-blue-700 bg-blue-50 rounded-lg px-3 py-2 border border-blue-100">
              <Lock className="inline w-3 h-3 mr-1" /> Leave a field blank to keep its existing saved value.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-semibold text-gray-700">Username</Label>
                <Input
                  type="password" autoComplete="new-password"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={status?.masked_username ? 'Leave blank to keep existing' : 'Barbour username'}
                  className="font-mono text-sm"
                  data-testid="barbour-edit-username"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-700">Password</Label>
                <Input
                  type="password" autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={status?.masked_password ? 'Leave blank to keep existing' : 'Barbour password'}
                  className="font-mono text-sm"
                  data-testid="barbour-edit-password"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-700">API Key</Label>
                <Input
                  type="password" autoComplete="new-password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={status?.masked_api_key ? 'Leave blank to keep existing' : 'Barbour x-api-key'}
                  className="font-mono text-sm"
                  data-testid="barbour-edit-api-key"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Direct API vs Zoho Function toggle */}
      <div className="rounded-xl border bg-white p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Zap className="w-4 h-4 text-amber-600" />
            </div>
            <div className="min-w-0">
              <h4 className="text-sm font-bold text-gray-900">Use Direct Barbour API</h4>
              <p className="text-xs text-gray-500 mt-0.5 max-w-md">
                ON (default): the whole app — including Terra — searches Barbour using the API
                credentials above. Turn OFF to make everything use the Zoho CRM "Barbour_Search"
                function instead — this also hides "Search Barbour" (V1) from the CRM dropdown,
                leaving only "Barbour Search V2" there.
              </p>
              {loadingStatus ? null : (
                <p className="text-xs font-semibold mt-1.5" style={{ color: status?.use_direct_api === false ? '#d97706' : '#059669' }}>
                  Currently: {status?.use_direct_api === false ? 'Zoho CRM function (V2) everywhere' : 'Direct Barbour API (V1) everywhere'}
                </p>
              )}
            </div>
          </div>
          <Switch
            checked={status?.use_direct_api !== false}
            onCheckedChange={onToggleDirectApi}
            disabled={loadingStatus || savingToggle}
            data-testid="barbour-use-direct-api-toggle"
          />
        </div>
      </div>

      {/* Barbour Search V2 — default radius */}
      <div className="rounded-xl border bg-white p-4" data-testid="barbour-search-v2-settings">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-9 h-9 rounded-lg bg-cyan-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <MapPin className="w-4 h-4 text-cyan-600" />
          </div>
          <div className="min-w-0">
            <h4 className="text-sm font-bold text-gray-900">Barbour Search V2</h4>
            <p className="text-xs text-gray-500 mt-0.5 max-w-md">
              Default radius (in miles) used whenever "Barbour Search V2" runs without an explicit radius —
              across the CRM dropdown search modals and Terra's tools. Defaults to 0.1 miles. The max radius
              is the ceiling enforced everywhere a Barbour search radius is used — V1, V2 and Terra alike.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-2 pl-12">
          <div className="w-32">
            <label className="text-xs text-gray-500 mb-1 block">Default Radius (miles)</label>
            <Input
              type="number"
              min="0.05"
              max="100"
              step="0.05"
              value={v2Radius}
              onChange={(e) => setV2Radius(e.target.value)}
              disabled={loadingStatus}
              data-testid="barbour-v2-default-radius-input"
            />
          </div>
          <Button
            size="sm"
            onClick={onSaveV2Radius}
            disabled={loadingStatus || savingV2Radius}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="barbour-v2-default-radius-save-btn"
          >
            {savingV2Radius ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save
          </Button>
          <div className="w-32">
            <label className="text-xs text-gray-500 mb-1 block">Max Radius (miles)</label>
            <Input
              type="number"
              min="0.1"
              max="100"
              step="0.5"
              value={maxRadius}
              onChange={(e) => setMaxRadius(e.target.value)}
              disabled={loadingStatus}
              data-testid="barbour-max-radius-input"
            />
          </div>
          <Button
            size="sm"
            onClick={onSaveMaxRadius}
            disabled={loadingStatus || savingMaxRadius}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="barbour-max-radius-save-btn"
          >
            {savingMaxRadius ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save
          </Button>
        </div>
      </div>

      {/* Search test tool */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-1">Project Search (Test)</h4>
        <p className="text-sm text-gray-500 mb-3">
          Enter a postcode and radius to find nearby Barbour ABI projects, using the credentials above.
          Results are the closest matches first.
        </p>

        <Card>
          <CardContent className="py-4 px-4 space-y-3">
            <div className="flex flex-wrap gap-2 items-end">
              <div className="flex-1 min-w-[180px]">
                <label className="text-xs text-gray-500 mb-1 block">Postcode</label>
                <Input
                  placeholder="e.g. SW1A 1AA"
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value)}
                  onKeyDown={handleKeyDown}
                  data-testid="barbour-postcode-input"
                />
              </div>
              <div className="w-28">
                <label className="text-xs text-gray-500 mb-1 block">Radius (miles)</label>
                <Input
                  type="number"
                  min="0.5"
                  max={maxRadius || 100}
                  step="0.5"
                  value={radius}
                  onChange={(e) => setRadius(e.target.value)}
                  onKeyDown={handleKeyDown}
                  data-testid="barbour-radius-input"
                />
              </div>
              <Button onClick={search} disabled={searching || !postcode.trim()} data-testid="barbour-search-btn">
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-3.5 h-3.5 mr-1" /> Search</>}
              </Button>
            </div>

            {error && (
              <p className="text-sm text-red-600" data-testid="barbour-search-error">{error}</p>
            )}

            {results && (
              <div data-testid="barbour-search-results">
                <p className="text-xs text-gray-500 mb-2">
                  Showing {results.count} of {results.total_matching} project{results.total_matching === 1 ? '' : 's'} within {results.radius_miles} mile{results.radius_miles === 1 ? '' : 's'} of {results.postcode}
                </p>
                {results.projects.length === 0 ? (
                  <p className="text-sm text-gray-400 py-4 text-center">No projects found in that radius.</p>
                ) : (
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                        <tr>
                          <th className="text-left px-3 py-2">Barbour ID</th>
                          <th className="text-left px-3 py-2">Project Name</th>
                          <th className="text-left px-3 py-2">Address / Postcode</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {results.projects.map((p) => (
                          <tr key={p.barbour_id} data-testid={`barbour-result-row-${p.barbour_id}`}>
                            <td className="px-3 py-2 font-mono text-gray-700">{p.barbour_id}</td>
                            <td className="px-3 py-2 text-gray-900">{p.project_name}</td>
                            <td className="px-3 py-2 text-gray-500 flex items-center gap-1">
                              <MapPin className="w-3 h-3 shrink-0" />
                              {[p.address, p.postcode].filter(Boolean).join(', ')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BarbourIntegrationSettings;
